import { useContext } from 'react'
import { Outlet } from 'react-router'
import { Alert, Box, CircularProgress, Grid, useTheme } from '@mui/material'

import { NavContext } from '../../providers/nav-provider'
import useIsMobile from '../../utils/hooks/useIsMobile'
import Footer from '../footer'
import Header from '../header'
import Sidenav from '../sidenav'

import classes from './index.styles'

interface ScreenProps {
  loading?: boolean
  error?: string
  shouldConstrainWidth?: boolean
  /** Header component if it is NOT the default header. If it is, use `headerProps` instead */
  header?: React.ReactNode
  showHeader?: boolean
  /** Props for the default header. Ignored if `header` is provided */
  headerProps?: typeof Header extends React.ComponentType<infer P> ? P : never
  footer?: React.ReactNode
  /** Space to add to the container's bottom padding to account for a fixed footer */
  footerSpacing?: number
  /** Makes the desktop outlet column shrink to fit its content instead of stretching to full height */
  outletAlignStart?: boolean
  children: React.ReactNode | React.ReactNode[]
}

const DashboardScreen = ({
  loading,
  error,
  shouldConstrainWidth = true,
  header,
  showHeader = true,
  headerProps,
  footer,
  footerSpacing,
  outletAlignStart,
  children,
  ...rest
}: ScreenProps) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { sidenavOpen } = useContext(NavContext)

  // Compute the children column size: when sidenav collapses, absorb its grid columns
  const childrenSize = sidenavOpen ? { sm: 12, md: 4 } : { sm: 12, md: 7, lg: 6 }

  return (
    <Grid container={true} sx={classes.containerRoot} {...rest}>
      {showHeader && (header || <Header {...headerProps} showHamburger={!isMobile} />)}

      {!isMobile ? (
        <Grid container={true} columnSpacing={3} rowSpacing={3} sx={classes.desktopContentGrid(theme)}>
          {sidenavOpen && (
            <Grid size={{ sm: 12, md: 3, lg: 2 }} sx={classes.sidenavGridItem(theme)}>
              <Sidenav />
            </Grid>
          )}

          <Grid size={childrenSize} sx={classes.childrenGridItem(theme)}>
            {loading ? (
              <Grid container={true} size={12} sx={classes.loadingContainer}>
                <CircularProgress />
              </Grid>
            ) : error ? (
              <Alert severity="error" sx={classes.errorAlert}>
                {error}
              </Alert>
            ) : (
              children
            )}
          </Grid>

          <Grid size={{ sm: 12, md: 5, lg: 6 }} sx={classes.outletGridItem(theme, outletAlignStart)}>
            <Outlet />
          </Grid>
        </Grid>
      ) : (
        <Grid container={true} sx={classes.mobileContainer(theme, shouldConstrainWidth)}>
          <Grid sx={classes.mobileContentGrid(footerSpacing)}>
            {loading ? (
              <Grid container={true} size={12} sx={classes.loadingContainer}>
                <CircularProgress />
              </Grid>
            ) : error ? (
              <Alert severity="error" sx={classes.errorAlert}>
                {error}
              </Alert>
            ) : (
              <>
                {children}
                <Outlet />
                <Box sx={{ height: '96px' }} /> {/* Spacer to ensure content is not hidden behind fixed Footer */}
              </>
            )}
          </Grid>
        </Grid>
      )}

      {isMobile && <>{footer || <Footer />}</>}
    </Grid>
  )
}

export default DashboardScreen
